/**
 *  <image-maximize target="magic-zoom.main-image" gallery=".alt-images-container">
 *
 *  Corner button that opens a full-viewport modal showing the current
 *  main-image zoom variant, with navigation arrows + zoom controls +
 *  thumbnail strip when the page has alt images. Matches the reference
 *  UX (Sirv-style Theodore Alexander viewer).
 *
 *  Attributes:
 *    target  — CSS selector for the "current image" source. Reads its
 *              `zoom-src` attribute to determine which image is active
 *              on open. Default "magic-zoom".
 *    gallery — CSS selector for a container that holds `<alt-image>`
 *              children (each with data-zoom-src + a nested <img> that
 *              provides the thumbnail). Default ".alt-images-container".
 *              If missing or empty, modal opens in single-image mode
 *              (close + zoom controls only, no arrows, no thumbs).
 *
 *  Interactions:
 *    - Close: ×, backdrop click, Escape key
 *    - Prev/next: side arrows, Left/Right arrow keys
 *    - Zoom: −/+ buttons, +/= and − keys, mousewheel over image
 *    - Pan when zoomed: click-and-drag
 *    - Jump-to-image: click a thumbnail
 *
 *  Modal is a lazy singleton — one is appended to <body> on first open
 *  and reused thereafter.
 */
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.75;

export default class ImageMaximizeElement extends HTMLElement {
    connectedCallback() {
        this._targetSel = this.getAttribute('target') || 'magic-zoom';
        this._gallerySel = this.getAttribute('gallery') || '.alt-images-container';
        this._render();
    }

    _render() {
        // "arrows-out" 4-corner glyph — compact version matching the
        // reference. Hooks pulled inward from the edge (4/20 in a 24-unit
        // viewBox) with 5-unit flanks and stems ending at (15,9)/etc so
        // each arrow reads as a small "K" pointing outward, with a clear
        // gap in the center.
        this.innerHTML =
            '<button type="button" class="image-maximize-btn" aria-label="Maximize image">' +
              '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' +
                // top-right corner
                '<polyline points="15 4 20 4 20 9"></polyline>' +
                '<line x1="20" y1="4" x2="15" y2="9"></line>' +
                // top-left corner
                '<polyline points="9 4 4 4 4 9"></polyline>' +
                '<line x1="4" y1="4" x2="9" y2="9"></line>' +
                // bottom-right corner
                '<polyline points="20 15 20 20 15 20"></polyline>' +
                '<line x1="20" y1="20" x2="15" y2="15"></line>' +
                // bottom-left corner
                '<polyline points="4 15 4 20 9 20"></polyline>' +
                '<line x1="4" y1="20" x2="9" y2="15"></line>' +
              '</svg>' +
            '</button>';
        this.querySelector('button').addEventListener('click', this._open.bind(this));
    }

    _open(event) {
        event.preventDefault();
        event.stopPropagation();

        // Build the gallery (list of {zoomSrc, thumbSrc}) from surrounding
        // alt-image elements. Falls back to a single-item gallery pulled
        // from the target element's zoom-src.
        var gallery = this._collectGallery();
        if (!gallery.length) return;

        // Which item is currently active? Prefer the alt-image with
        // "active" class; otherwise the one whose zoom-src matches the
        // target element's current zoom-src; otherwise index 0.
        // The "active" preference was documented but never implemented — the
        // loop matched only the host zoom-src, which the thumb swap did not
        // update, so the modal always opened on the page's initial image.
        var currentZoomSrc = this._targetZoomSrc();
        var activeIndex = -1;
        for (var i = 0; i < gallery.length; i++) {
            if (gallery[i].active) { activeIndex = i; break; }
        }
        if (activeIndex < 0) {
            for (var j = 0; j < gallery.length; j++) {
                if (gallery[j].zoomSrc === currentZoomSrc) { activeIndex = j; break; }
            }
        }
        if (activeIndex < 0) activeIndex = 0;

        var modal = ImageMaximizeElement._modal();
        modal._setGallery(gallery, activeIndex);
        modal.classList.add('open');
        document.body.classList.add('image-maximize-open');
    }

    _targetZoomSrc() {
        var target = this._resolveTarget();
        if (!target) return null;
        return target.getAttribute('zoom-src') || target.getAttribute('src') || null;
    }

    /**
     *  Enumerate alt-image elements in the gallery container. Each yields
     *  { zoomSrc, thumbSrc }:
     *   - zoomSrc: from data-zoom-src (fallback: data-src, then child img src)
     *   - thumbSrc: from the nested <img> src (falls back to zoomSrc)
     *  If no alt-images are found, returns a single-entry list built from
     *  the target element's current zoom-src (so single-image PDPs like
     *  fabric still open cleanly).
     */
    _collectGallery() {
        var gallery = [];
        var container = document.querySelector(this._gallerySel);
        if (container) {
            var alts = container.querySelectorAll('alt-image');
            for (var i = 0; i < alts.length; i++) {
                var alt = alts[i];
                var zoomSrc = alt.getAttribute('data-zoom-src')
                    || alt.getAttribute('data-src')
                    || (alt.querySelector('img') && alt.querySelector('img').getAttribute('src'));
                var img = alt.querySelector('img');
                var thumbSrc = (img && img.getAttribute('src')) || zoomSrc;
                if (zoomSrc) gallery.push({ zoomSrc: zoomSrc, thumbSrc: thumbSrc, active: alt.classList.contains('active') });
            }
        }
        if (!gallery.length) {
            // Fallback for PDPs without an alt-image carousel (e.g.
            // fabric detail). Read both zoom-src (2100) and src (800/1200)
            // from the target element so the thumb uses the smaller
            // variant instead of downloading the full-size 2100 for a
            // 60px thumbnail.
            var target = this._resolveTarget();
            if (target) {
                var zoomSrc = target.getAttribute('zoom-src') || target.getAttribute('src');
                var thumbSrc = target.getAttribute('src') || zoomSrc;
                if (zoomSrc) gallery.push({ zoomSrc: zoomSrc, thumbSrc: thumbSrc });
            }
        }
        return gallery;
    }

    _resolveTarget() {
        var target = null;
        if (this.parentElement) target = this.parentElement.querySelector(this._targetSel);
        if (!target) target = document.querySelector(this._targetSel);
        return target;
    }

    /**
     *  Lazy singleton modal. Wires all listeners once and exposes
     *  _setGallery(items, activeIndex) for callers to (re-)populate
     *  before showing.
     */
    static _modal() {
        if (ImageMaximizeElement._modalRef) return ImageMaximizeElement._modalRef;

        var modal = document.createElement('div');
        modal.className = 'image-maximize-modal';
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        modal.innerHTML =
            '<button type="button" class="image-maximize-modal__close" aria-label="Close">' +
              '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' +
                '<line x1="5" y1="5" x2="19" y2="19"></line>' +
                '<line x1="19" y1="5" x2="5" y2="19"></line>' +
              '</svg>' +
            '</button>' +
            '<button type="button" class="image-maximize-modal__nav image-maximize-modal__nav--prev" aria-label="Previous image">' +
              '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' +
                '<polyline points="15 6 9 12 15 18"></polyline>' +
              '</svg>' +
            '</button>' +
            '<button type="button" class="image-maximize-modal__nav image-maximize-modal__nav--next" aria-label="Next image">' +
              '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' +
                '<polyline points="9 6 15 12 9 18"></polyline>' +
              '</svg>' +
            '</button>' +
            '<div class="image-maximize-modal__viewport">' +
              '<img class="image-maximize-modal__img" alt="" draggable="false" />' +
            '</div>' +
            '<div class="image-maximize-modal__zoom">' +
              '<button type="button" class="image-maximize-modal__zoom-btn" data-zoom="out" aria-label="Zoom out">' +
                '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><line x1="6" y1="12" x2="18" y2="12"/></svg>' +
              '</button>' +
              '<button type="button" class="image-maximize-modal__zoom-btn" data-zoom="in" aria-label="Zoom in">' +
                '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><line x1="6" y1="12" x2="18" y2="12"/><line x1="12" y1="6" x2="12" y2="18"/></svg>' +
              '</button>' +
            '</div>' +
            '<div class="image-maximize-modal__thumbs"></div>';

        // ----- state (kept on the modal instance itself) -----
        modal._gallery = [];
        modal._index = 0;
        modal._zoom = 1;
        modal._panX = 0;
        modal._panY = 0;
        modal._dragging = false;

        var img = modal.querySelector('.image-maximize-modal__img');
        var viewport = modal.querySelector('.image-maximize-modal__viewport');
        var thumbsEl = modal.querySelector('.image-maximize-modal__thumbs');
        var prevBtn = modal.querySelector('.image-maximize-modal__nav--prev');
        var nextBtn = modal.querySelector('.image-maximize-modal__nav--next');
        var zoomOutBtn = modal.querySelector('[data-zoom="out"]');
        var zoomInBtn = modal.querySelector('[data-zoom="in"]');
        var closeBtn = modal.querySelector('.image-maximize-modal__close');

        // ----- state helpers -----
        function applyTransform() {
            img.style.transform =
                'translate(' + modal._panX + 'px, ' + modal._panY + 'px) scale(' + modal._zoom + ')';
        }
        function updateZoomButtons() {
            zoomOutBtn.disabled = modal._zoom <= MIN_ZOOM + 0.001;
            zoomInBtn.disabled = modal._zoom >= MAX_ZOOM - 0.001;
        }
        function resetZoom() {
            modal._zoom = 1;
            modal._panX = 0;
            modal._panY = 0;
            applyTransform();
            updateZoomButtons();
            updateCursor();
        }
        // Cursor communicates what clicking will do:
        //   zoom-in  -> click will zoom in one step
        //   zoom-out -> click will zoom all the way back out (at MAX)
        //   grabbing -> currently panning
        function updateCursor() {
            viewport.classList.toggle('is-max', modal._zoom >= MAX_ZOOM - 0.001);
        }
        function setImage(index) {
            if (!modal._gallery.length) return;
            var n = modal._gallery.length;
            modal._index = ((index % n) + n) % n; // wrap
            img.src = modal._gallery[modal._index].zoomSrc;
            resetZoom();
            renderThumbs();
        }
        function renderThumbs() {
            var n = modal._gallery.length;
            // Nav arrows only when there's actually another image to
            // switch to. Thumbnails render whenever there's at least one
            // image — the main image is always shown as a thumb even in
            // single-image mode (matches the PDP's alt-images carousel
            // which also renders the main image as the first thumb).
            modal.classList.toggle('has-multi', n > 1);
            if (n < 1) { thumbsEl.innerHTML = ''; return; }

            // Only re-render the thumb <img>s if the gallery contents
            // actually changed. Cheap identity check: length + first
            // zoomSrc. Otherwise just update the active class so we
            // don't reload thumbnails on every image switch.
            var sig = n + '|' + modal._gallery[0].zoomSrc;
            if (thumbsEl.dataset.sig !== sig) {
                thumbsEl.dataset.sig = sig;
                var html = '';
                for (var i = 0; i < n; i++) {
                    html += '<button type="button" class="image-maximize-modal__thumb" data-i="' + i + '">' +
                            '<img src="' + modal._gallery[i].thumbSrc + '" alt="" />' +
                            '</button>';
                }
                thumbsEl.innerHTML = html;
            }
            var buttons = thumbsEl.querySelectorAll('.image-maximize-modal__thumb');
            for (var j = 0; j < buttons.length; j++) {
                buttons[j].classList.toggle('is-active', j === modal._index);
            }
        }
        function zoomBy(delta) {
            var next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, modal._zoom + delta));
            if (next === modal._zoom) return;
            modal._zoom = next;
            if (modal._zoom <= 1.001) { modal._panX = 0; modal._panY = 0; }
            applyTransform();
            updateZoomButtons();
            updateCursor();
        }
        // Click-to-zoom: step up until MAX; once at MAX, next click
        // returns all the way to MIN. Matches the two-cursor state
        // (zoom-in / zoom-out) so the cursor always predicts the click.
        function toggleZoom() {
            if (modal._zoom >= MAX_ZOOM - 0.001) {
                resetZoom();
            } else {
                zoomBy(ZOOM_STEP);
            }
        }
        function close() {
            modal.classList.remove('open');
            document.body.classList.remove('image-maximize-open');
            img.removeAttribute('src');
            resetZoom();
        }

        // ----- expose setGallery for callers -----
        modal._setGallery = function(gallery, activeIndex) {
            modal._gallery = gallery;
            setImage(activeIndex || 0);
        };

        // ----- events -----
        closeBtn.addEventListener('click', close);
        prevBtn.addEventListener('click', function() { setImage(modal._index - 1); });
        nextBtn.addEventListener('click', function() { setImage(modal._index + 1); });
        zoomOutBtn.addEventListener('click', function() { zoomBy(-ZOOM_STEP); });
        zoomInBtn.addEventListener('click', function() { zoomBy(ZOOM_STEP); });

        // Backdrop click closes (only when clicking chrome, not the image itself)
        modal.addEventListener('click', function(e) {
            if (e.target === modal) close();
        });

        // Keyboard
        document.addEventListener('keydown', function(e) {
            if (!modal.classList.contains('open')) return;
            if (e.key === 'Escape') close();
            else if (e.key === 'ArrowLeft') setImage(modal._index - 1);
            else if (e.key === 'ArrowRight') setImage(modal._index + 1);
            else if (e.key === '+' || e.key === '=') zoomBy(ZOOM_STEP);
            else if (e.key === '-' || e.key === '_') zoomBy(-ZOOM_STEP);
        });

        // Wheel zoom over the image
        viewport.addEventListener('wheel', function(e) {
            if (!modal.classList.contains('open')) return;
            e.preventDefault();
            zoomBy(e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
        }, { passive: false });

        // Click vs. drag differentiation on the viewport:
        //   - mousedown starts tracking
        //   - move > DRAG_THRESHOLD engages pan mode (only when zoomed)
        //   - mouseup without exceeding threshold = click = toggleZoom()
        //
        // Drag-to-pan only works when already zoomed (nothing to pan at 1x).
        // At 1x, movement is ignored; the mouseup fires toggleZoom either way.
        var DRAG_THRESHOLD = 5; // px
        var startX = 0, startY = 0, startPanX = 0, startPanY = 0;
        viewport.addEventListener('mousedown', function(e) {
            // Ignore clicks that originated on the image? No — image
            // clicks should zoom too. Ignore non-primary buttons.
            if (e.button !== 0) return;
            e.preventDefault();
            modal._mouseDown = true;
            modal._dragging = false;
            startX = e.clientX; startY = e.clientY;
            startPanX = modal._panX; startPanY = modal._panY;
        });
        window.addEventListener('mousemove', function(e) {
            if (!modal._mouseDown) return;
            var dx = e.clientX - startX;
            var dy = e.clientY - startY;
            if (!modal._dragging) {
                // Not dragging yet — only engage drag once the mouse
                // moves beyond threshold AND we're zoomed.
                if (modal._zoom > 1.001 && (dx*dx + dy*dy) > DRAG_THRESHOLD * DRAG_THRESHOLD) {
                    modal._dragging = true;
                    viewport.classList.add('is-dragging');
                } else {
                    return;
                }
            }
            modal._panX = startPanX + dx;
            modal._panY = startPanY + dy;
            applyTransform();
        });
        window.addEventListener('mouseup', function(e) {
            if (!modal._mouseDown) return;
            modal._mouseDown = false;
            var wasDragging = modal._dragging;
            modal._dragging = false;
            viewport.classList.remove('is-dragging');
            // Treat mouseup as a click iff we never engaged drag. Only
            // fire when the mouseup target is inside the viewport, so
            // releasing on chrome (e.g. close button while accidentally
            // held over) doesn't zoom.
            if (!wasDragging && viewport.contains(e.target)) {
                toggleZoom();
            }
        });

        // Thumbnail click delegation
        thumbsEl.addEventListener('click', function(e) {
            var btn = e.target.closest ? e.target.closest('.image-maximize-modal__thumb') : null;
            if (!btn) return;
            var i = parseInt(btn.getAttribute('data-i'), 10);
            if (!isNaN(i)) setImage(i);
        });

        document.body.appendChild(modal);
        ImageMaximizeElement._modalRef = modal;
        return modal;
    }
}

customElements.define('image-maximize', ImageMaximizeElement);
